import { supabase } from '../lib/supabase';
import { getAuthSessionHeaders } from './sessionService';

const ARTICLE_PROCESS_TIMEOUT_MS = 150000; // 2 minutes 30 seconds
const GENERATION_LEASE_SECONDS = 180;
const PUBLISHING_LEASE_SECONDS = 90;
const MAX_GENERATION_ATTEMPTS = 2;
const MAX_PUBLISHING_ATTEMPTS = 3;

/**
 * 🚀 ARTICLE QUEUE WORKER
 *
 * This worker is a thin dispatcher, deliberately: it claims the next
 * eligible article via the SAME atomic claim_article_attempt() RPC the
 * server-side cron (process-queue) uses, invokes generate-article, and then
 * gets out of the way. It does NOT:
 *   - reset "stuck" articles itself (that used to race with process-queue's
 *     own stuck-recovery and with the original invocation possibly still
 *     running server-side — two independent stuck-detectors that didn't
 *     know about each other was exactly how the same article could get
 *     billed for OpenAI generation more than once)
 *   - write status='completed'/'error' after the Edge Function call
 *     (generate-article is the sole authority for every status transition;
 *     this worker only reflects what the DB already says, via realtime/poll)
 *
 * Reaping abandoned leases is exclusively process-queue's job (it runs
 * reliably every 2 minutes server-side); this worker only ever tries to
 * claim rows that are already eligible (queued / content_ready / a
 * retryable error whose backoff has elapsed) — the atomic RPC guarantees
 * this worker and the cron worker can never end up processing the same row.
 */

class ArticleQueueWorker {
  constructor() {
    this.isProcessing     = false;
    this.subscription     = null;
    this.started          = false;
    this.nextTickTimer    = null;
    this._onVisibilityChange = null;
  }

  // ─────────────────────────────────────────────────────────────────
  //  START
  // ─────────────────────────────────────────────────────────────────
  async start() {
    if (this.started) {
      console.log('⚠️ Article Queue Worker already started — skipping start()');
      return;
    }

    console.log('🚀 Article Queue Worker starting...');
    this.started = true;

    if (this.subscription) {
      try { this.subscription?.unsubscribe?.(); } catch (_) {}
      this.subscription = null;
    }

    // ── Realtime: react to INSERT / UPDATE ──────────────────────────
    this.subscription = supabase
      ?.channel('wp_articles_queue_worker')
      ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'wp_articles' },
        (payload) => {
          if (payload?.new?.status === 'queued') {
            console.log('🆕 Article queued (INSERT):', payload?.new?.id);
            this.processQueue();
          }
        })
      ?.on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'wp_articles' },
        (payload) => {
          if (['queued', 'content_ready'].includes(payload?.new?.status)) {
            console.log('🔄 Article eligible again (UPDATE):', payload?.new?.id, payload?.new?.status);
            this.processQueue();
          }
        })
      ?.subscribe();

    // ── Re-check on wake / tab focus ─────────────────────────────────
    // No longer resets anything itself — just asks the queue to look for
    // eligible work again. If the article this tab was working on is truly
    // abandoned, process-queue's lease reaper (server-side, always running)
    // will make it eligible again; this worker never guesses.
    this._onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab became visible — checking queue...');
        this.isProcessing = false;
        this.processQueue();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    console.log('✅ Article Queue Worker started successfully');
    this.processQueue();
  }

  // ─────────────────────────────────────────────────────────────────
  //  STOP
  // ─────────────────────────────────────────────────────────────────
  stop() {
    this.started = false;

    if (this.nextTickTimer) {
      clearTimeout(this.nextTickTimer);
      this.nextTickTimer = null;
    }

    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      this._onVisibilityChange = null;
    }

    if (this.subscription) {
      this.subscription?.unsubscribe?.();
      this.subscription = null;
      console.log('⏹️ Article Queue Worker stopped');
    } else {
      console.log('⏹️ Article Queue Worker stopped (no active subscription)');
    }
  }

  // ─────────────────────────────────────────────────────────────────
  //  PROCESS QUEUE  (FIFO)
  // ─────────────────────────────────────────────────────────────────
  async processQueue() {
    if (!this.started) return;

    if (this.isProcessing) {
      console.log('⏳ Already processing an article, skipping...');
      return;
    }

    try {
      this.isProcessing = true;

      // Global guard: if this user already has an article in flight, wait —
      // matches process-queue's "one article at a time per user" FIFO rule.
      const { data: inFlight } = await supabase
        ?.from('wp_articles')
        ?.select('id')
        ?.in('status', ['generating', 'publishing'])
        ?.limit(1);
      if (inFlight && inFlight.length > 0) return;

      const nowIso = new Date().toISOString();
      const { data: candidates, error } = await supabase
        ?.from('wp_articles')
        ?.select('id, status')
        ?.or(
          `status.eq.queued,status.eq.content_ready,` +
          `and(status.eq.generation_error,retryable.eq.true,next_retry_at.lte.${nowIso}),` +
          `and(status.eq.publishing_error,retryable.eq.true,next_retry_at.lte.${nowIso})`
        )
        ?.order('created_at', { ascending: true })
        ?.limit(1)
        ?.maybeSingle();

      if (error) {
        console.error('❌ Queue fetch error:', error);
        return;
      }

      if (!candidates) {
        console.log('✅ Queue is empty');
        return;
      }

      console.log(`🔄 Processing article: ${candidates.id} (currently ${candidates.status})`);
      await this.processArticle(candidates);

    } catch (error) {
      console.error('❌ Queue processing error:', error);
    } finally {
      this.isProcessing = false;

      if (this.started) {
        this.nextTickTimer = setTimeout(() => this.processQueue(), 1000);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────
  //  PROCESS A SINGLE ARTICLE
  // ─────────────────────────────────────────────────────────────────
  async processArticle(candidate) {
    const articleId = candidate?.id;
    if (!articleId) {
      console.error('❌ Invalid article payload:', candidate);
      return;
    }

    const stage = (candidate.status === 'content_ready' || candidate.status === 'publishing_error')
      ? 'publishing'
      : 'generation';

    try {
      // Atomic claim — the SAME RPC process-queue uses. If another worker
      // (this tab's own cron-driven backend, another browser tab, or a
      // concurrent cron tick) already claimed this row, claimedRows comes
      // back empty and we just move on; no local state to reconcile.
      const { data: claimedRows, error: claimErr } = await supabase.rpc('claim_article_attempt', {
        p_article_id: articleId,
        p_stage: stage,
        p_lease_seconds: stage === 'generation' ? GENERATION_LEASE_SECONDS : PUBLISHING_LEASE_SECONDS,
        p_max_attempts: stage === 'generation' ? MAX_GENERATION_ATTEMPTS : MAX_PUBLISHING_ATTEMPTS,
      });

      if (claimErr) {
        console.error(`❌ claim_article_attempt error for ${articleId}:`, claimErr.message);
        return;
      }
      if (!claimedRows || claimedRows.length === 0) {
        console.log(`⚠️ Article ${articleId} was already claimed elsewhere — skipping`);
        return;
      }

      console.log(`⚙️ Calling Edge Function for article ${articleId} (stage=${stage})...`);

      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) throw new Error('No active session for Edge Function call');

      const authHeaders = await getAuthSessionHeaders();

      const { data: generatedData, error: fnError } = await supabase?.functions?.invoke(
        'generate-article',
        {
          body: { articleId },
          headers: authHeaders,
          timeout: ARTICLE_PROCESS_TIMEOUT_MS
        }
      );

      console.log('📥 Edge Function response received:', {
        hasData: !!generatedData,
        hasError: !!fnError,
        errorType: fnError?.name,
        statusCode: fnError?.context?.status,
      });

      // Deliberately no DB writes here on either success or failure.
      // generate-article already wrote the correct status/attempt bookkeeping
      // itself before responding (completed / content_ready / *_error), and
      // if the HTTP round-trip itself fails/times out client-side (the
      // function can still be running server-side up to its own 150s limit —
      // see generate-article's leases), the article's lease_expires_at is
      // what determines whether it's actually abandoned, not this tab
      // guessing from a failed fetch. process-queue's reaper (server-side,
      // always running) is the only thing that ever resolves that.
      if (fnError) {
        console.warn(`⚠️ Edge Function call for ${articleId} did not complete cleanly (${fnError?.message}); leaving status/lease as-is for the reaper to resolve if it's genuinely stuck.`);
      } else if (generatedData?.imageMetadata) {
        await this.saveImageMetadata(articleId, generatedData.imageMetadata);
      }

    } catch (error) {
      console.error(`❌ Failed to dispatch article ${articleId}:`, error);
    }
  }

  // ─────────────────────────────────────────────────────────────────
  //  SAVE IMAGE METADATA
  // ─────────────────────────────────────────────────────────────────
  async saveImageMetadata(articleId, imageMetadata) {
    try {
      const { data: article } = await supabase
        ?.from('wp_articles')
        ?.select('image_main_url, image_top_url, image_middle_url, image_end_url')
        ?.eq('id', articleId)
        ?.maybeSingle();
      if (!article) return;

      const imageRecords = [];
      const locations = ['main', 'top', 'middle', 'end'];

      for (const location of locations) {
        const imageUrl = article?.[`image_${location}_url`];
        const metadata = imageMetadata?.[location];
        if (imageUrl && metadata) {
          imageRecords?.push({
            article_id: articleId,
            location,
            webp_url: imageUrl,
            alt_text: metadata?.alt_text,
            title: metadata?.title,
            caption: metadata?.caption,
            description: metadata?.description,
            original_filename: imageUrl?.split('/')?.pop(),
          });
        }
      }

      if (imageRecords?.length > 0) {
        const { error } = await supabase?.from('wp_article_images')?.insert(imageRecords);
        if (error) {
          console.error('Failed to save image metadata:', error);
        } else {
          console.log(`💾 Saved ${imageRecords?.length} image metadata records`);
        }
      }
    } catch (error) {
      console.error('Error saving image metadata:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────
  //  HELPERS
  // ─────────────────────────────────────────────────────────────────
  triggerProcessing() {
    console.log('🔧 Manual queue processing triggered');
    this.processQueue();
  }

  async getQueueStatus() {
    const { data: queued }     = await supabase?.from('wp_articles')?.select('id', { count: 'exact' })?.eq('status', 'queued');
    const { data: inFlight }   = await supabase?.from('wp_articles')?.select('id', { count: 'exact' })?.in('status', ['generating', 'publishing']);
    return {
      queued:       queued?.length || 0,
      processing:   inFlight?.length || 0,
      isProcessing: this.isProcessing,
      started:      this.started,
    };
  }
}

// ─────────────────────────────────────────────────────────────────
//  SINGLETON + AUTO-START
// ─────────────────────────────────────────────────────────────────
export const articleQueueWorker = new ArticleQueueWorker();

if (typeof window !== 'undefined') {
  supabase?.auth?.getSession()?.then(({ data: { session } }) => {
    if (session) articleQueueWorker?.start();
  });

  supabase?.auth?.onAuthStateChange((event, session) => {
    if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
      articleQueueWorker?.start();
    } else if (event === 'SIGNED_OUT') {
      articleQueueWorker?.stop();
    }
  });
}
